/**
 * disposal_report_recipients — Phase 16 / Task 16.5 (DEC-162).
 *
 * Multi-recipient list per (disposal_company, facility|digest) scope for
 * custom report delivery. Admin-managed by DISPOSAL_ADMIN users of the
 * disposal tenant. Backs the `module_custom_reports` toggle
 * (DEC-005 / DEC-158).
 *
 * SEPARATE table from disposal_daily_email_recipients (Q3 DEC-162): reports
 * and daily-email are distinct feature surfaces; a disposal company may
 * want different distribution lists (e.g. daily-email to site managers +
 * accounting, custom reports to auditors only). Keeping them separate also
 * lets a future admin sub-role gate them independently.
 *
 * Scope semantics (mirror of 16.4 daily-email recipients):
 *   - facility_id NOT NULL → recipient gets reports from single-facility
 *     templates that target their facility.
 *   - facility_id     NULL → recipient gets ALL reports for the company
 *     (regardless of the template's facility scope).
 *
 * App-layer FK guard: Postgres does NOT allow CHECK subqueries; the
 * create-recipient route SELECTs before INSERT to confirm the facility
 * belongs to the caller's disposal_company. Cross-company → 400
 * DISPOSAL_REPORT_RECIPIENT_FACILITY_MISMATCH.
 *
 * Uniqueness: partial UNIQUE index on (company, LOWER(email),
 * COALESCE(facility_id, zero-uuid)) WHERE opted_out_at IS NULL. Same
 * zero-uuid COALESCE sentinel trick from 16.4 for NULL-in-UNIQUE-key.
 *
 * Soft-delete via opted_out_at + opted_out_reason:
 *   - UNSUBSCRIBE_TOKEN — recipient clicked the CAN-SPAM one-click link.
 *   - ADMIN_REMOVED     — DISPOSAL_ADMIN removed via DELETE /:id.
 *
 * SEPARATE HMAC secret (REPORT_UNSUBSCRIBE_HMAC_SECRET vs 16.4's
 * UNSUBSCRIBE_HMAC_SECRET) — blast-radius isolation: a compromised
 * report-unsubscribe secret cannot be used against daily-email recipients.
 *
 * RLS: tenant-isolated via subquery to disposal_companies.tenant_id.
 */

import {
  check,
  index,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { disposalCompanies, disposalFacilities } from './disposals.js';
import { users } from './users.js';

export const disposalReportRecipients = pgTable(
  'disposal_report_recipients',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    facilityId: uuid('facility_id').references(() => disposalFacilities.id, {
      onDelete: 'cascade',
    }),
    email: text('email').notNull(),
    addedAt: timestamp('added_at', { withTimezone: true }).notNull().defaultNow(),
    addedByUserId: uuid('added_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    optedOutAt: timestamp('opted_out_at', { withTimezone: true }),
    optedOutReason: text('opted_out_reason'),
  },
  (t) => [
    check('disposal_report_recipients_email_lower_check', sql`LOWER(email) = email`),
    check(
      'disposal_report_recipients_reason_check',
      sql`opted_out_reason IN ('UNSUBSCRIBE_TOKEN','ADMIN_REMOVED') OR opted_out_reason IS NULL`,
    ),
    uniqueIndex('disposal_report_recipients_active_idx')
      .on(
        t.disposalCompanyId,
        sql`LOWER(email)`,
        sql`COALESCE(facility_id, '00000000-0000-0000-0000-000000000000'::uuid)`,
      )
      .where(sql`opted_out_at IS NULL`),
    index('disposal_report_recipients_company_idx')
      .on(t.disposalCompanyId)
      .where(sql`opted_out_at IS NULL`),
    pgPolicy('disposal_report_recipients_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`disposal_company_id IN (
        SELECT dc.id FROM disposal_companies dc
        WHERE dc.tenant_id::text = current_setting('app.current_tenant_id', true)
      )`,
    }),
  ],
);

export type DisposalReportRecipient = typeof disposalReportRecipients.$inferSelect;
export type NewDisposalReportRecipient = typeof disposalReportRecipients.$inferInsert;
export const disposalReportRecipientSelectSchema = createSelectSchema(disposalReportRecipients);
export const disposalReportRecipientInsertSchema = createInsertSchema(disposalReportRecipients);
